import React from 'react';
import { ArrowRight, Info, Navigation, Target } from 'lucide-react';
import type { DetrixSession, DetrixUserStep } from '../../types';
import { ErrorNarrative } from '../../components/ErrorNarrative';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { formatSessionOffset } from '../../utils/time';

interface StepInspectorProps {
  session: DetrixSession;
  step: DetrixUserStep;
}

export const StepInspector: React.FC<StepInspectorProps> = ({ session, step }) => {
  const { setActiveTab, setSelectedId } = useSessionUiStore();

  const handleJumpToEvent = (eventId: string) => {
    setActiveTab('events');
    setSelectedId(eventId);
  };

  const isErr = step.kind === 'error';

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--bg-panel)]">
      <ErrorNarrative session={session} />
      {isErr ? (
        <div className="shrink-0 border-b border-[var(--inspector-error-border)] bg-[var(--inspector-error-bg)] px-3 py-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--inspector-error-title)]">Step failed</div>
          <p className="mt-1 text-[12px] text-[var(--inspector-error-body)]">{step.title}</p>
        </div>
      ) : null}
      <div className="border-b p-3">
        <h3
          className={`mb-1 text-[14px] font-semibold ${isErr ? 'text-[var(--error)]' : 'text-[var(--text-primary)]'}`}
        >
          {step.title}
        </h3>
        {step.subtitle ? <p className="text-[11px] text-[var(--text-secondary)]">{step.subtitle}</p> : null}
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overflow-x-hidden p-3">
        <section>
          <h4 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase text-[var(--text-muted)]">
            <Info className="h-3 w-3" /> Overview
          </h4>
          <div className="space-y-1">
            <KVRow label="Kind" value={step.kind} />
            <KVRow label="Start" value={formatSessionOffset(step.timestampStart)} />
            <KVRow label="End" value={formatSessionOffset(step.timestampEnd)} />
            {step.navigationMode ? <KVRow label="Nav mode" value={step.navigationMode} /> : null}
            {step.path ? <KVRow label="Path" value={step.path} /> : null}
          </div>
        </section>

        {step.target ? (
          <section>
            <h4 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase text-[var(--text-muted)]">
              <Target className="h-3 w-3" /> Target
            </h4>
            <div className="break-all rounded border bg-[var(--bg-editor)] p-2 font-mono text-[11px] text-[var(--accent)]">
              {step.target}
            </div>
          </section>
        ) : null}

        <section>
          <h4 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase text-[var(--text-muted)]">
            <Navigation className="h-3 w-3" /> Source events
          </h4>
          <div className="space-y-1">
            {step.sourceEventIds.map((eventId) => (
              <button
                key={eventId}
                type="button"
                onClick={() => handleJumpToEvent(eventId)}
                className="group flex w-full items-center justify-between rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1.5 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--border)]"
              >
                <span className="font-mono text-[var(--text-secondary)]">{eventId}</span>
                <div className="flex items-center gap-1 text-[var(--accent)] opacity-0 transition-opacity group-hover:opacity-100">
                  <span>Jump</span>
                  <ArrowRight className="h-3 w-3" />
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const KVRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-2 font-mono text-[11px]">
    <span className="w-[100px] shrink-0 text-[var(--text-secondary)]">{label}:</span>
    <span className="text-[var(--text-primary)]">{value}</span>
  </div>
);
